import { Link } from "@tanstack/react-router";
import {
  MAIN_MARKETS_LABEL,
  SERVICE_AREAS,
  VIRTUAL_CONSULT_PATH,
  serviceAreaCityLine,
  servingLabel,
  type ServiceArea,
  type ServiceAreaKey,
} from "@/lib/service-areas";

// The text side of a location card for an out-of-market office (see
// service-areas.ts): "Serving <city>, <state>", the address, the main
// locations and a virtual consultation. No phone number, no directions link.
// Main-market cards keep their own markup.
export function ServiceAreaCardBody({
  area,
  headingTag: Heading = "h3",
  headingHref,
  size = "sm",
}: {
  area: ServiceAreaKey | ServiceArea;
  headingTag?: "h3" | "h4";
  /** Links the heading to the city page, as the clinic cards did. */
  headingHref?: string;
  /** "lg" on a city page's own card, "sm" in a list of cards. */
  size?: "sm" | "lg";
}) {
  const a = typeof area === "string" ? SERVICE_AREAS[area] : area;
  const label = servingLabel(a);
  return (
    <div className="flex flex-col" data-card="service-area">
      <Heading
        className={`inline-block self-start border-b-2 border-primary pb-1 font-bold text-foreground ${
          size === "lg" ? "text-2xl" : "text-lg md:text-xl"
        }`}
      >
        {headingHref ? (
          <Link to={headingHref as string} className="transition-colors hover:text-primary">
            {label}
          </Link>
        ) : (
          label
        )}
      </Heading>
      <div className={`mt-4 text-foreground/80 ${size === "lg" ? "text-base" : "text-sm"}`}>
        <p>{a.street}</p>
        <p>{serviceAreaCityLine(a)}</p>
        <p className="mt-3">
          <span className="font-semibold text-foreground">Main Locations:</span>{" "}
          {MAIN_MARKETS_LABEL}
        </p>
      </div>
      <a
        href={VIRTUAL_CONSULT_PATH}
        aria-label={`Book a virtual consultation for ${a.name}`}
        className="mt-6 inline-block self-start bg-primary px-5 py-3 text-center text-sm font-bold uppercase tracking-wider text-primary-foreground transition hover:bg-primary/90"
      >
        Book a Virtual Consultation
      </a>
    </div>
  );
}
